import React, { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';

/**
 * Shape returned by `POST /api/report_problem/refine`.
 * The endpoint is side-effect free: it only asks Claude to clean up the
 * user's draft and classify it — it does NOT submit the report. The user
 * previews/edits the result here, then submits via `/api/report_problem`.
 */
export interface RefineResult {
  refined_description: string;
  summary?: string;
  category?: string;
  // Fault domain proposed by the AI triage (Support Pathway addendum) —
  // e.g. 'control_mismatch', 'equipment_internal', 'workmanship'.
  fault_domain?: string | null;
  likely_owner?: string;
  is_powston_scope?: boolean;
  triage_note?: string;
  // Trading-strategy triage (inverter-intelligence #1404). Which of the four
  // categories the pre-submit check thinks the report falls into, and what the
  // captured interval does / does not establish. All advisory: none of them may
  // hide, delay, or gate the path to filing the report.
  strategy_classification?: string | null;
  interval_establishes?: string | null;
  interval_does_not_establish?: string | null;
}

interface RefineReportButtonProps {
  /** Current draft the user has typed. */
  description: string;
  /**
   * Build the full context payload to send for refinement. Each report flow
   * supplies its own fields (action context vs. feedback context).
   */
  buildPayload: () => Record<string, any>;
  /** Apply the (possibly edited) refined text back into the parent textarea. */
  onApply: (refined: string) => void;
  /**
   * Surface the full refine result (category, fault_domain, triage note) to
   * the parent so the submit body can carry the AI-proposed fault domain.
   * Called with null when the user discards the suggestion.
   */
  onResult?: (result: RefineResult | null) => void;
  /** Lock refinement and preview editing while the parent submits its snapshot. */
  disabled?: boolean;
}

const RefineReportButton: React.FC<RefineReportButtonProps> = ({
  description,
  buildPayload,
  onApply,
  onResult,
  disabled = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RefineResult | null>(null);
  const [editedText, setEditedText] = useState('');
  const [error, setError] = useState('');

  const canRefine = description.trim().length > 0 && !loading && !disabled;

  const handleRefine = async () => {
    if (!canRefine) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/report_problem/refine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      if (!response.ok) throw new Error(`Refine failed: ${response.status}`);
      const data: RefineResult = await response.json();
      setResult(data);
      setEditedText(data.refined_description || description);
      onResult?.(data);
    } catch (e) {
      // Degrade gracefully — the user can still submit their own text.
      setError("AI preview is unavailable right now — you can submit your report as written.");
    } finally {
      setLoading(false);
    }
  };

  const handleUse = () => {
    onApply(editedText);
    // The AI metadata (fault_domain/category) only stays valid for the text
    // the AI actually produced — a preview edit before "Use this" invalidates
    // it, same rule as manual edits in the parent textarea.
    onResult?.(editedText === result?.refined_description ? result : null);
    setResult(null);
  };

  const handleDiscard = () => {
    setResult(null);
    setError('');
    onResult?.(null);
  };

  return (
    <div className="my-3">
      <button
        type="button"
        onClick={handleRefine}
        disabled={!canRefine}
        aria-disabled={!canRefine}
        title={!canRefine ? 'Write a description first' : 'Get an AI-improved version to preview before submitting'}
        className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded border transition-colors
          border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300
          bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40
          ${!canRefine ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {loading ? 'Improving…' : 'Preview with AI'}
      </button>

      {error && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">{error}</p>
      )}

      {result && (
        <div className="mt-3 p-3 rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50/60 dark:bg-blue-900/20">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">AI-improved report</p>
            {result.category && result.category !== 'Unclear' && (
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                {result.category}
              </span>
            )}
          </div>

          <textarea
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            disabled={disabled}
            rows={5}
            className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
          />

          {result.triage_note && (
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              {result.triage_note}
              {result.likely_owner && (
                <span className="ml-1 font-medium text-gray-700 dark:text-gray-300">
                  Likely owner: {result.likely_owner}.
                </span>
              )}
            </p>
          )}

          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={handleDiscard}
              disabled={disabled}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 transition-colors"
            >
              Keep mine
            </button>
            <button
              type="button"
              onClick={handleUse}
              disabled={disabled}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 dark:bg-blue-700 rounded hover:bg-blue-700 dark:hover:bg-blue-800 transition-colors"
            >
              Use this
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefineReportButton;
